<template>
  <article class="js-product-card ProductCard" ref="card" itemscope itemtype="https://schema.org/Product" itemprop="itemListElement" :data-product-id="productId" :data-variant-id="variantId">
    <div class="ProductCard-ImageWrapper" :class="loadedMedia">
      <a :href="cardUrl">
        <picture v-if="featuredImage">
          <img loading="lazy" itemprop="image" @load="transitionMedia" :src="featuredImage.url" :alt="featuredImage.altText" />
        </picture>
        <ProductCardBadging :item="item" />
      </a>
      <button v-if="quickViewReady" :data-ht-element="quickActionProperty" role="button" class="ProductCard__QuickView" @click="handleQuickAction">
        <Icon icon="qv-plus" />
        <span class="t5" data-color="night-sky-dark">
          {{ quickActionText }}
        </span>
      </button>
    </div>
    <a :href="cardUrl">
      <Eyebrow :item="item" :ready="ready" :location="'product-card'" />
      <p class="t2" data-ht-element="product-title">
        {{ item.title }}
      </p>
      <Price :item="item" :location="'product-card'" :ready="ready" />
      <div v-if="shortDescription" class="p4" v-html="shortDescription"></div>
      <ReviewStars v-if="reviewsProduct" :product="reviewsProduct" :location="'product-card'" />
    </a>
    <ProductCardSwatches v-if="swatches.length > 0" @updateCard="updateCard" :swatches="swatches" :all-options="item.options" :selected-options="featuredVariant.selectedOptions" :selection-listener="false" :hidden-options="[]" :url="cardUrl" />
    <Teleport to="#quickViewModal" v-if="modalOpen && modalItem">
      <ProductModal @toggle-modal="toggleModal" :product="modalItem" location="quick-view" :reviewsProduct="reviewsProduct" :terms="terms" />
    </Teleport>
  </article>
</template>

<script setup>
// Vue core
import { ref, onMounted, computed } from 'vue';

// Utilities
import { deepClone } from '@utils/performance';
import { formatID, parseTag } from '@utils/format';
import { fetchStorefront } from '@utils/GraphQLClient';

// Composables
import { VARIANT_FROM_SELECTIONS } from './Composables/Queries';
import { quickAdd } from '../Global/Composables/ProductCard';

// Global components
import Icon from '../Global/Icon.vue';
import Price from '../Global/Price.vue';
import ReviewStars from '../Global/ReviewStars.vue';
import ProductCardSwatches from '../Global/ProductCardSwatches.vue';
import Eyebrow from '../Global/Eyebrow.vue';

// Local components
import ProductModal from '../Product/ProductModal.vue';
import ProductCardBadging from './ProductCardBadging.vue';

const observer = ref(null);
const card = ref(null);
const swatches = ref([]);
const inView = ref(false);
const loaded = ref(false);
const interacted = ref(false);
const modalOpen = ref(false);
const modalItem = ref(false);

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  selectionListener: {
    type: Boolean,
    default: false,
  },
  ready: {
    type: Boolean,
    default: false,
  },
  appliedFilters: {
    type: Array,
    required: false,
    default: [],
  },
  terms: {
    type: Object,
    required: false,
    default: {},
  },
});

const featuredImage = computed(() => {
  const variant = featuredVariant.value ?? false;
  if (!variant) return false;

  const image = variant.image;
  return image;
});

const productId = formatID(props?.item?.id) || '';
let variant = window.location.pathname.includes('/collections') ? props?.item?.featuredSku?.reference : props?.item?.selectedVariant;
const variantId = variant?.id ? formatID(variant?.id) : productId;

const featuredVariant = computed(() => {
  const item = props?.item || {};
  const variants = item?.variants.nodes || false;
  if (!variants) return false;

  const appliedFilters = props.appliedFilters.length > 0 ? true : false;
  const colorFilterApplied = appliedFilters && props.appliedFilters.filter((facet) => facet.variantMetafield && facet.variantMetafield.key === 'color_group').length > 0;

  let variant = variants[0];

  if (!colorFilterApplied) {
    const hasFeaturedSku = item.featuredSku?.reference ?? false;
    variant = hasFeaturedSku && !interacted.value ? item.featuredSku.reference : variants[0];
  }

  return variant;
});

const cardUrl = computed(() => {
  const tags = props.item.tags ?? [];
  const base = `/products/${props.item.handle}`;
  const variant = featuredVariant.value;
  if (!variant) return base;

  const isBundle = tags.includes('Product Bundle') ?? false;
  const isSteppedBundle = isBundle && !tags.includes('Auto-Bundle');
  const selectedVariant = formatID(variant.id);
  const param = isSteppedBundle ? `component0` : `variant`;
  return `${base}?${param}=${selectedVariant}`;
});

const shortDescription = computed(() => {
  const description = props.item.shortDescription?.value ?? '';
  if (description === '') return false;

  return description;
});

const reviewsProduct = computed(() => {
  const tags = props.item.tags ?? [];
  const hideReviews = tags.includes('Hide Zero Reviews');
  if (hideReviews) return false;

  const tag = tags.filter((tag) => tag.includes('Review ProductID')) ?? [];
  return tag ? parseTag(tags, 'Review ProductID') : formatID(props.item.id);
});

const isSingleProduct = computed(() => {
  return props.item.options.find((option) => option.values.includes('Default Title')) ? true : false;
});

const quickActionText = computed(() => {
  const text = isSingleProduct.value ? `Quick Add` : `Quick View`;
  return text;
});

const quickActionProperty = computed(() => {
  const property = isSingleProduct.value ? `quick-add` : `quick-view`;
  return `${property}-button`;
});

const updateCard = async (selection) => {
  const { selectedOptions } = featuredVariant.value;
  const colorIndex = selectedOptions.findIndex((option) => option.name === 'Color');

  const currentColor = selectedOptions[colorIndex].value;

  if (currentColor === selection.value) return;

  interacted.value = true;

  const updatedOptions = deepClone(selectedOptions).map((option) => {
    if (option.name === 'Color') {
      return selection;
    }
    return option;
  });

  const request = {
    query: VARIANT_FROM_SELECTIONS,
    variables: {
      handle: props.item.handle,
      options: updatedOptions,
    },
  };

  const { data } = await fetchStorefront(request);

  props.item.variants.nodes = [data.product.variantBySelectedOptions];
};

const loadedMedia = computed(() => (inView.value && loaded.value ? 'ProductCard-ImageWrapper--Loaded' : ''));
const transitionMedia = () => (loaded.value = true);

const quickViewReady = computed(() => {
  const tags = props.item.tags ?? [];
  const isBundle = tags.find((tag) => tag.includes('Product Bundle')) ? true : false;
  if (loadedMedia.value !== '' && !isBundle) {
    return setTimeout(() => true, 400);
  }

  return false;
});

const unobserve = () => {
  if (observer.value) observer.value.disconnect();
};

const initializeObserver = () => {
  unobserve();
  observer.value = new IntersectionObserver(
    (entries) => {
      const { isIntersecting } = entries[0];
      if (isIntersecting) {
        inView.value = true;
        unobserve();
      }
    },
    { threshold: 0.6 }
  );
};

const observeCard = () => {
  unobserve();
  observer.value.observe(card.value);
};

const setSwatches = () => {
  const options = props?.item?.options || [];
  const color = options.find((option) => option.name === 'Color');
  if (!color) return;

  swatches.value = Array.from(new Set([...color.values]));
};

const toggleModal = (item) => {
  modalOpen.value = !modalOpen.value;

  if (modalOpen.value) {
    const current = deepClone(featuredVariant.value);
    modalItem.value = item;
    modalItem.value.selectedVariant = current;
    modalItem.value.selectedOptions = current.selectedOptions;
    modalItem.value.edit = true;
  } else {
    modalItem.value = false;
  }
};

const handleQuickAction = () => {
  const item = deepClone(props.item);
  return isSingleProduct.value ? quickAdd(item) : toggleModal(item);
};

onMounted(() => {
  setSwatches();
  initializeObserver();
  observeCard();
});
</script>
